import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import apiService from "../services/api";
import { getCurrentYearMonth, formatMonth, formatDateInPT } from "../utils/dateUtils";

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = {
  page: { padding: "1rem 0" },
  header: { marginBottom: "1.5rem" },
  h1: { fontSize: "1.875rem", fontWeight: "700", color: "#001272", marginBottom: "0.25rem" },
  subtitle: { color: "#001272", opacity: 0.8, fontSize: "1rem" },

  controls: {
    display: "flex", flexWrap: "wrap", gap: "0.75rem",
    alignItems: "center", marginBottom: "1.25rem",
  },
  label: { fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.2rem" },
  controlGroup: { display: "flex", flexDirection: "column" },
  input: {
    padding: "0.45rem 0.75rem", border: "1px solid #d1d5db",
    borderRadius: "0.375rem", fontSize: "0.875rem", outline: "none",
  },
  select: {
    padding: "0.45rem 0.75rem", border: "1px solid #d1d5db",
    borderRadius: "0.375rem", fontSize: "0.875rem", background: "white", outline: "none",
  },
  count: { marginLeft: "auto", color: "#6b7280", fontSize: "0.875rem", alignSelf: "flex-end" },

  // Desktop table
  tableWrap: { width: "100%", overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", background: "white", borderRadius: "0.5rem", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  th: { background: "#f8fafc", color: "#001272", fontWeight: "600", fontSize: "0.8rem", padding: "0.75rem 1rem", textAlign: "left", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" },
  td: { padding: "0.75rem 1rem", fontSize: "0.875rem", color: "#374151", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" },
  trEven: { background: "#fafbff" },

  // Mobile card
  card: {
    background: "white", borderRadius: "0.5rem", padding: "1rem",
    border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    marginBottom: "0.75rem",
  },
  cardTopic: { fontSize: "0.95rem", fontWeight: "600", color: "#001272", marginBottom: "0.5rem" },
  cardMeta: {
    display: "flex", flexWrap: "wrap", gap: "0.5rem",
    fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.75rem",
  },
  cardMetaItem: { display: "flex", alignItems: "center", gap: "0.2rem" },
  cardFooter: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" },
  cardActions: { display: "flex", gap: "0.4rem" },

  badge: (converted) => ({
    display: "inline-block", padding: "0.2rem 0.6rem", borderRadius: "9999px",
    fontSize: "0.75rem", fontWeight: "600",
    background: converted ? "#dcfce7" : "#f3f4f6",
    color: converted ? "#166534" : "#6b7280",
  }),

  actionBtn: (variant) => ({
    padding: "0.3rem 0.65rem", fontSize: "0.75rem", fontWeight: "500",
    border: "1px solid",
    borderRadius: "0.25rem", cursor: "pointer",
    background: variant === "primary" ? "#001272" : variant === "danger" ? "white" : "white",
    color: variant === "primary" ? "white" : variant === "danger" ? "#dc2626" : "#001272",
    borderColor: variant === "primary" ? "#001272" : variant === "danger" ? "#dc2626" : "#d1d5db",
  }),

  modal: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modalBox: {
    background: "white", borderRadius: "0.5rem", padding: "1.5rem",
    width: "100%", maxWidth: "420px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    margin: "1rem",
  },
  modalTitle: { fontSize: "1.1rem", fontWeight: "700", color: "#001272", marginBottom: "1rem" },
  modalLabel: { display: "block", fontSize: "0.85rem", color: "#374151", marginBottom: "0.3rem", fontWeight: "500" },
  modalInput: { width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem", outline: "none", boxSizing: "border-box" },
  modalTextarea: { width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem", outline: "none", boxSizing: "border-box", resize: "vertical", minHeight: "80px" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" },

  loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 0" },
  spinner: { border: "3px solid #f3f3f3", borderTop: "3px solid #53bdeb", borderRadius: "50%", width: "40px", height: "40px", animation: "spin 1s linear infinite", marginBottom: "1rem" },
  errBox: { background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "0.375rem", padding: "1rem", color: "#991b1b" },
  emptyState: { textAlign: "center", padding: "3rem 1rem", color: "#6b7280" },
};

// ─── Conversion Modal ─────────────────────────────────────────────────────────

const ConversionModal = ({ lesson, onClose, onSaved }) => {
  const isEdit = lesson.converted;
  const [yearMonth, setYearMonth] = useState(lesson.convertedYearMonth || "");
  const [notes, setNotes] = useState(lesson.conversionNotes || "");
  const [counselorId, setCounselorId] = useState(lesson.counselorId || "");
  const [saving, setSaving] = useState(false);

  const { data: tutorsPage } = useQuery({
    queryKey: ["allTutors"],
    queryFn: () => apiService.getTutors(0, 500),
    staleTime: 5 * 60 * 1000,
  });
  const counselors = tutorsPage?.content ?? [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        appointmentId: lesson.appointmentId,
        convertedYearMonth: yearMonth,
        notes,
        counselorId: counselorId || null,
      };
      if (isEdit) {
        await apiService.updateDemoLessonConversion(lesson.conversionId, payload);
        toast.success("Conversion updated");
      } else {
        await apiService.markDemoLessonConverted(payload);
        toast.success("Lesson marked as converted");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save conversion");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.modal} onClick={onClose}>
      <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
        <h2 style={s.modalTitle}>
          {isEdit ? "Edit Conversion" : "Mark as Converted"}
        </h2>
        <p style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: "1rem" }}>
          {lesson.topic}
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={s.modalLabel}>Converted Year-Month *</label>
            <input
              style={s.modalInput}
              type="month"
              value={yearMonth}
              onChange={(e) => setYearMonth(e.target.value)}
              required
            />
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label style={s.modalLabel}>Counselor (optional)</label>
            <select
              style={{ ...s.modalInput, background: "white" }}
              value={counselorId}
              onChange={(e) => setCounselorId(e.target.value)}
            >
              <option value="">— None —</option>
              {counselors
                .slice()
                .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                .map((t) => (
                  <option key={t.canonicalTutorId} value={t.canonicalTutorId}>
                    {t.firstName} {t.lastName}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label style={s.modalLabel}>Notes (optional)</label>
            <textarea
              style={s.modalTextarea}
              placeholder="Any notes about the conversion..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div style={s.modalActions}>
            <button
              type="button"
              style={s.actionBtn("secondary")}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ ...s.actionBtn("primary"), opacity: saving ? 0.7 : 1 }}
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Update" : "Mark Converted"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Lesson Actions ───────────────────────────────────────────────────────────

const LessonActions = ({ lesson, onEdit, onUndo }) =>
  lesson.converted ? (
    <>
      <button style={s.actionBtn("secondary")} onClick={() => onEdit(lesson)}>Edit</button>
      <button style={s.actionBtn("danger")} onClick={() => onUndo(lesson)}>Undo</button>
    </>
  ) : (
    <button style={s.actionBtn("primary")} onClick={() => onEdit(lesson)}>Mark Converted</button>
  );

// ─── Main Page ────────────────────────────────────────────────────────────────

const DemoLessonsPage = () => {
  const queryClient = useQueryClient();
  const [yearMonth, setYearMonth] = useState(getCurrentYearMonth());
  const [branchId, setBranchId] = useState("");
  const [convertedFilter, setConvertedFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [modalLesson, setModalLesson] = useState(null);

  const queryParams = {
    yearMonth: yearMonth || undefined,
    branchId: branchId || undefined,
    converted: convertedFilter === "" ? undefined : convertedFilter === "true",
  };

  const { data: lessons = [], isLoading, error } = useQuery({
    queryKey: ["demoLessons", yearMonth, branchId, convertedFilter],
    queryFn: () => apiService.getDemoLessons(queryParams),
    staleTime: 2 * 60 * 1000,
  });

  const filteredLessons = topicFilter
    ? lessons.filter((l) => (l.topic || "").toLowerCase().includes(topicFilter.toLowerCase()))
    : lessons;

  const handleUndo = async (lesson) => {
    if (!window.confirm("Remove conversion record for this lesson?")) return;
    try {
      await apiService.deleteDemoLessonConversion(lesson.conversionId);
      toast.success("Conversion removed");
      queryClient.invalidateQueries({ queryKey: ["demoLessons"] });
    } catch (err) {
      toast.error(err.message || "Failed to remove conversion");
    }
  };

  if (isLoading) return (
    <div style={s.page}>
      <div style={s.header}><h1 style={s.h1}>Demo Lessons</h1></div>
      <div style={s.loadingWrap}>
        <div style={s.spinner} />
        <p style={{ color: "#001272" }}>Loading demo lessons...</p>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={s.page}>
      <div style={s.header}><h1 style={s.h1}>Demo Lessons</h1></div>
      <div style={s.errBox}>Error loading demo lessons: {error.message}</div>
    </div>
  );

  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .demo-table-wrap { display: block; }
        .demo-cards { display: none; }
        @media (max-width: 640px) {
          .demo-table-wrap { display: none; }
          .demo-cards { display: block; }
          .demo-controls { flex-direction: column; align-items: stretch !important; }
          .demo-controls .demo-count { margin-left: 0 !important; }
        }
      `}</style>

      <div style={s.header}>
        <h1 style={s.h1}>Demo Lessons</h1>
        <p style={s.subtitle}>Track completed demo/trial lessons and their conversion status.</p>
        <p style={{ fontSize: "0.8rem", color: "#64748b", margin: "0.35rem 0 0 0" }}>
          Demo Lessons are synced from TC only once daily around 1:00 AM America/Los_Angeles time.
        </p>
      </div>

      {/* Filters */}
      <div className="demo-controls" style={s.controls}>
        <div style={s.controlGroup}>
          <span style={s.label}>Year-Month</span>
          <input
            style={s.input}
            type="month"
            value={yearMonth}
            onChange={(e) => setYearMonth(e.target.value)}
          />
        </div>
        <div style={s.controlGroup}>
          <span style={s.label}>Branch</span>
          <select style={s.select} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">All Branches</option>
            <option value="US">US</option>
            <option value="UAE">UAE</option>
          </select>
        </div>
        <div style={s.controlGroup}>
          <span style={s.label}>Status</span>
          <select style={s.select} value={convertedFilter} onChange={(e) => setConvertedFilter(e.target.value)}>
            <option value="">All</option>
            <option value="false">Not Converted</option>
            <option value="true">Converted</option>
          </select>
        </div>
        <div style={s.controlGroup}>
          <span style={s.label}>Topic</span>
          <input
            style={s.input}
            type="text"
            placeholder="Filter by topic..."
            value={topicFilter}
            onChange={(e) => setTopicFilter(e.target.value)}
          />
        </div>
        <span className="demo-count" style={s.count}>
          {filteredLessons.length} lesson{filteredLessons.length !== 1 ? "s" : ""}
        </span>
      </div>

      {filteredLessons.length === 0 ? (
        <div style={s.emptyState}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "0.5rem" }}>
            No demo lessons found
          </h3>
          <p style={{ fontSize: "0.875rem" }}>
            {yearMonth || branchId || convertedFilter || topicFilter
              ? "Try adjusting your filters."
              : "No demo lessons available yet."}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="demo-table-wrap" style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Date</th>
                  <th style={s.th}>Topic</th>
                  <th style={s.th}>Tutor</th>
                  <th style={s.th}>Branch</th>
                  <th style={s.th}>Month</th>
                  <th style={s.th}>Duration (h)</th>
                  <th style={s.th}>Counselor</th>
                  <th style={s.th}>Conversion</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLessons.map((lesson, idx) => (
                  <tr key={lesson.appointmentId} style={idx % 2 === 1 ? s.trEven : {}}>
                    <td style={s.td}>{formatDateInPT(lesson.startTime)}</td>
                    <td style={s.td}>{lesson.topic}</td>
                    <td style={s.td}>
                      {[lesson.tutorFirstName, lesson.tutorLastName].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td style={s.td}>{lesson.branchId}</td>
                    <td style={s.td}>{formatMonth(lesson.yearMonth)}</td>
                    <td style={s.td}>{lesson.units != null ? lesson.units.toFixed(1) : "—"}</td>
                    <td style={s.td}>{lesson.counselorName || "—"}</td>
                    <td style={s.td}>
                      {lesson.converted ? (
                        <span style={s.badge(true)}>Converted {lesson.convertedYearMonth}</span>
                      ) : (
                        <span style={s.badge(false)}>Not Converted</span>
                      )}
                    </td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                      <LessonActions lesson={lesson} onEdit={setModalLesson} onUndo={handleUndo} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="demo-cards">
            {filteredLessons.map((lesson) => {
              const tutor = [lesson.tutorFirstName, lesson.tutorLastName].filter(Boolean).join(" ") || "—";
              return (
                <div key={lesson.appointmentId} style={s.card}>
                  <div style={s.cardTopic}>{lesson.topic || "—"}</div>
                  <div style={s.cardMeta}>
                    <span style={s.cardMetaItem}>{formatDateInPT(lesson.startTime)}</span>
                    <span style={{ color: "#d1d5db" }}>·</span>
                    <span style={s.cardMetaItem}>{tutor}</span>
                    <span style={{ color: "#d1d5db" }}>·</span>
                    <span style={s.cardMetaItem}>{lesson.branchId}</span>
                    <span style={{ color: "#d1d5db" }}>·</span>
                    <span style={s.cardMetaItem}>
                      {lesson.units != null ? `${lesson.units.toFixed(1)}h` : "—"}
                    </span>
                  </div>
                  {lesson.counselorName && (
                    <div style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.5rem" }}>
                      Counselor: {lesson.counselorName}
                    </div>
                  )}
                  <div style={s.cardFooter}>
                    {lesson.converted ? (
                      <span style={s.badge(true)}>Converted {lesson.convertedYearMonth}</span>
                    ) : (
                      <span style={s.badge(false)}>Not Converted</span>
                    )}
                    <div style={s.cardActions}>
                      <LessonActions lesson={lesson} onEdit={setModalLesson} onUndo={handleUndo} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {modalLesson && (
        <ConversionModal
          lesson={modalLesson}
          onClose={() => setModalLesson(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["demoLessons"] })}
        />
      )}
    </div>
  );
};

export default DemoLessonsPage;
